import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	useEffect,
	useRef,
	ReactNode,
	MouseEvent,
} from "react";
import { createPortal } from "react-dom";
import { AlertColor } from "@mui/material";
import Toaster from "../components/reusable-components/toaster/Toaster";
import ToastDeck, { iToastDeckItem } from "../components/reusable-components/toaster/ToastDeck";
import NotificationCard from "../components/reusable-components/notification-card/NotificationCard";
import { iNotificationRowModel } from "../types/notificationTypes";

type iCloseToasterCallback = undefined | (() => void);

export type ToastAction = { label: string; onClick: () => void; disabled?: boolean };

export type ToastOptions = {
	title?: string;
	autoHideDuration?: number;
	icon?: ReactNode;
	action?: ToastAction;
	loading?: boolean;
};

export type ToastContent = { message: string; title?: string; action?: ToastAction };

export type PromiseToastStates<T> = {
	loading: ToastContent;
	success: (value: T) => ToastContent;
	error: (error: unknown) => ToastContent;
};

interface iToastContextProp {
	showToast: (
		message: string,
		closeCallback?: iCloseToasterCallback,
		severity?: AlertColor,
		options?: ToastOptions,
	) => void;
	showPromiseToast: <T>(promise: Promise<T>, states: PromiseToastStates<T>) => void;
	showNotificationToast: (
		notification: iNotificationRowModel,
		onOpen: (event: MouseEvent<HTMLAnchorElement>) => void,
	) => void;
}

type NotificationToast = {
	key: number;
	notification: iNotificationRowModel;
	onOpen: (event: MouseEvent<HTMLAnchorElement>) => void;
};

const NOTIFICATION_TOAST_MS = 5000;
const MAX_NOTIFICATION_TOASTS = 3;

const ToastContext = createContext<iToastContextProp | null>(null);

const toastHandlerRef: {
	current: iToastContextProp["showToast"] | null;
} = { current: null };

export function showToastGlobal(
	message: string,
	closeCallback?: iCloseToasterCallback,
	severity?: AlertColor,
	options?: ToastOptions,
): void {
	if (toastHandlerRef.current) {
		toastHandlerRef.current(message, closeCallback, severity, options);
	}
}

export const useToast = (): iToastContextProp => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastContextProvider");
	}
	return context;
};

interface iToastContextProviderProps {
	children: ReactNode;
}

const ToastContextProvider: React.FC<iToastContextProviderProps> = ({ children }) => {
	const [open, setOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [title, setTitle] = useState<string | undefined>(undefined);
	const [icon, setIcon] = useState<ReactNode>(undefined);
	const [action, setAction] = useState<ToastAction | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [toastKey, setToastKey] = useState(0);
	const toastKeyRef = useRef(0);
	const openRef = useRef(false);
	const [autoHideDuration, setAutoHideDuration] = useState(4200);
	const [variant, setVariant] = useState<AlertColor | undefined>(undefined);
	const closeCallbackRef = useRef<iCloseToasterCallback>(undefined);
	const [notificationToasts, setNotificationToasts] = useState<NotificationToast[]>([]);
	const [statusOrder, setStatusOrder] = useState(0);
	const [deckExpanded, setDeckExpanded] = useState(false);
	const toastOrderRef = useRef(0);

	const handleOpenToast = useCallback(
		(
			nextMessage: string,
			nextCloseCallback: iCloseToasterCallback = () => {},
			nextVariant: AlertColor = "success",
			options?: ToastOptions,
			keepKey = false,
		) => {
			setMessage(nextMessage);
			setTitle(options?.title);
			setIcon(options?.icon);
			setAction(options?.action);
			setLoading(options?.loading ?? false);
			if (!keepKey) {
				toastKeyRef.current += 1;
				setToastKey(toastKeyRef.current);
				toastOrderRef.current += 1;
				setStatusOrder(toastOrderRef.current);
			}
			setAutoHideDuration(options?.autoHideDuration ?? 4200);
			setVariant(nextVariant);
			openRef.current = true;
			setOpen(true);
			closeCallbackRef.current = nextCloseCallback;
		},
		[],
	);

	const handleCloseToast = useCallback(() => {
		const callback = closeCallbackRef.current;
		closeCallbackRef.current = undefined;
		openRef.current = false;
		setOpen(false);
		if (callback) callback();
	}, []);

	const handlePromiseToast = useCallback(
		<T,>(promise: Promise<T>, states: PromiseToastStates<T>) => {
			const { message: loadingMessage, ...loadingOptions } = states.loading;
			handleOpenToast(loadingMessage, undefined, "info", { ...loadingOptions, loading: true });
			const key = toastKeyRef.current;
			const isStillShown = () => openRef.current && toastKeyRef.current === key;
			promise.then(
				(value) => {
					if (!isStillShown()) return;
					const { message: nextMessage, ...options } = states.success(value);
					handleOpenToast(nextMessage, undefined, "success", options, true);
				},
				(error) => {
					const { message: nextMessage, ...options } = states.error(error);
					handleOpenToast(nextMessage, undefined, "error", options, isStillShown());
				},
			);
		},
		[handleOpenToast],
	);

	useEffect(() => {
		toastHandlerRef.current = handleOpenToast;
		return () => {
			toastHandlerRef.current = null;
		};
	}, [handleOpenToast]);

	const closeNotificationToast = useCallback(
		(key: number) => setNotificationToasts((toasts) => toasts.filter((toast) => toast.key !== key)),
		[],
	);

	const handleNotificationToast = useCallback<iToastContextProp["showNotificationToast"]>(
		(notification, onOpen) => {
			toastOrderRef.current += 1;
			const key = toastOrderRef.current;
			setNotificationToasts((toasts) =>
				[
					{ key, notification, onOpen },
					...toasts.filter((toast) => toast.notification.id !== notification.id),
				].slice(0, MAX_NOTIFICATION_TOASTS),
			);
		},
		[],
	);
	const contextValue = useMemo(
		() => ({
			showToast: handleOpenToast,
			showPromiseToast: handlePromiseToast,
			showNotificationToast: handleNotificationToast,
		}),
		[handleOpenToast, handlePromiseToast, handleNotificationToast],
	);

	const statusShown = open && !!(message || title);
	const expanded = deckExpanded && (statusShown || notificationToasts.length > 0);

	const deckItems: iToastDeckItem[] = [
		...(statusShown
			? [
					{
						key: "status",
						order: statusOrder,
						node: (
							<Toaster
								inline
								paused={expanded}
								isOpen={open}
								onClose={handleCloseToast}
								message={message}
								title={title}
								icon={icon}
								action={action}
								loading={loading}
								toastKey={toastKey}
								variant={variant}
								autoHideDuration={autoHideDuration}
							/>
						),
					},
				]
			: []),
		...notificationToasts.map(({ key, notification, onOpen }) => ({
			key: `notification-${key}`,
			order: key,
			node: (
				<Toaster
					inline
					paused={expanded}
					isOpen
					toastKey={key}
					message={undefined}
					variant="info"
					className="ycToaster--notification"
					autoHideDuration={NOTIFICATION_TOAST_MS}
					onClose={() => closeNotificationToast(key)}
				>
					<NotificationCard
						notification={notification}
						onOpen={(event) => {
							closeNotificationToast(key);
							onOpen(event);
						}}
					/>
				</Toaster>
			),
		})),
	].sort((a, b) => b.order - a.order);

	return (
		<ToastContext.Provider value={contextValue}>
			{children}
			{createPortal(
				<ToastDeck items={deckItems} expanded={expanded} onExpandedChange={setDeckExpanded} />,
				document.body,
			)}
		</ToastContext.Provider>
	);
};

export default ToastContextProvider;
